import { z } from 'zod'
import { communityTag } from './community-tags'
import { discussionEntryHref } from './discussion-entry-location'
import { withLocalePath } from './i18n-utils'

export const searchResultType = z.enum([
  'discussion',
  'work',
  'evidence',
  'work_comment',
])
export const communitySearchQuery = z
  .object({
    workspace: z
      .string()
      .regex(/^[a-z0-9-]{1,80}$/)
      .default('research'),
    q: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .refine((value) =>
        [...value].every(
          (character) =>
            character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127,
        ),
      ),
    tag: communityTag.optional(),
    type: searchResultType.optional(),
    cursor: z.string().max(500).optional(),
  })
  .strict()
export const searchResultsSchema = z.object({
  workspace: z.string(),
  visibility: z.enum(['public', 'private']),
  items: z
    .array(
      z.object({
        id: z.string(),
        type: searchResultType,
        parentId: z.string().nullable(),
        title: z.string(),
        excerpt: z.string().max(280),
        createdAt: z.string(),
        author: z.string().nullable(),
        status: z.string(),
        tags: z.array(communityTag).max(8),
      }),
    )
    .max(20),
  nextCursor: z.string().nullable(),
  untrustedContent: z.literal(true),
})
export type CommunitySearchResults = z.infer<typeof searchResultsSchema>
export function searchResultHref(
  result: CommunitySearchResults['items'][number],
  locale: string,
  workspace: string,
  visibility: 'public' | 'private',
) {
  if (result.type === 'discussion')
    return discussionEntryHref(
      locale,
      result.parentId ?? result.id,
      result.id,
      visibility === 'private' ? workspace : undefined,
    )
  const id = result.type === 'work' ? result.id : result.parentId!
  if (visibility === 'private')
    return `${withLocalePath('/community/private', locale)}?workspace=${encodeURIComponent(workspace)}#work-${id}`
  return `${withLocalePath(`/community/work/${id}`, locale)}${result.type === 'evidence' ? '#task-evidence' : result.type === 'work_comment' ? '#task-discussion' : ''}`
}
